import { FC, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  Grid,
  IconButton,
  InputAdornment,
  Skeleton,
  TextField,
  Typography,
} from '@mui/material';

import CloseIcon from 'assets/icons/cabinet/delete-icon.svg';
import SearchIcon from 'assets/icons/search.svg';
import SortIcon from 'assets/icons/Sort_Icon.svg';
import { getMediaType } from './hooks/useDrafts/utils';
import { getMimeTypeFromFileName, getRowIcon } from 'utils/helpers/fileCabinetHelpers';
import {
  FileCabinetTypeEnum,
  IFolder,
  useGetFileCabinetFolderItemsQuery,
  useGetProjectFileCabinetByIdQuery,
} from 'redux-store/api/file-cabinet';
import { resolveFileCabinetIds } from 'utils/services/fileCabinetService';
import { IMediaItem } from '_interfaces/types/post.types';

type Props = {
  open: boolean;
  projectId: number;
  attachedFileCabinetIds: number[];
  onClose: () => void;
  onAddFiles: (items: IMediaItem[]) => void;
};

type PickerRowProps = {
  item: IFolder;
  projectId: number;
  level: number;
  attachedIds: Set<number>;
  expandedFolderIds: Set<number>;
  selectedFiles: Record<number, IFolder>;
  sortAscending: boolean;
  onToggleFile: (file: IFolder) => void;
  onToggleFolder: (folderId: number) => void;
};

const toMediaItem = (file: IFolder, url: string, mimeType?: string | null): IMediaItem => {
  const resolvedMimeType = mimeType || file.mimeType || getMimeTypeFromFileName(file.name);

  return {
    fileCabinetId: file.id,
    url,
    name: file.name,
    mimeType: resolvedMimeType,
    type: getMediaType(file.name),
  };
};

const sortItems = (items: IFolder[], sortAscending: boolean) =>
  [...items].sort((a, b) =>
    sortAscending ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name)
  );

const sortAndFilterItems = (items: IFolder[], searchValue: string, sortAscending: boolean) => {
  const normalizedSearch = searchValue.trim().toLowerCase();
  const searchedItems = normalizedSearch
    ? items.filter((item) => item.name?.toLowerCase().includes(normalizedSearch))
    : items;

  return sortItems(searchedItems, sortAscending);
};

const hasKnownEmptyFolder = (item: IFolder) =>
  item.itemsCount !== undefined && item.itemsCount !== null && Number(item.itemsCount) === 0;

const isInsideFolder = (file: IFolder, folderId: number) => {
  const targetFolderId = String(folderId);
  const fileCabinetPath = Array.isArray(file.fileCabinetPath)
    ? (file.fileCabinetPath as { id: string | number }[])
    : [];

  return (
    String(file.parentId) === targetFolderId ||
    fileCabinetPath.some((pathItem) => String(pathItem.id) === targetFolderId)
  );
};

const SelectionBox: FC<{ selected?: boolean; disabled?: boolean }> = ({ selected, disabled }) => (
  <Box
    sx={{
      width: '28px',
      height: '28px',
      border: `1.5px solid ${selected ? '#536DFE' : '#D9DEEA'}`,
      borderRadius: '8px',
      backgroundColor: selected ? '#536DFE' : '#FFFFFF',
      color: '#FFFFFF',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '20px',
      lineHeight: 1,
      opacity: disabled ? 0.5 : 1,
    }}
  >
    {selected ? '✓' : null}
  </Box>
);

const LoadingRows: FC<{ count?: number; level?: number }> = ({ count = 3, level = 0 }) => (
  <>
    {Array.from({ length: count }).map((_, index) => (
      <Grid
        key={index}
        sx={{
          display: 'grid',
          gridTemplateColumns: '42px minmax(0, 1fr) 44px',
          alignItems: 'center',
          padding: '14px 16px',
          paddingLeft: `${16 + level * 28}px`,
          borderBottom: '1px solid #EEF1F6',
          minHeight: '64px',
        }}
      >
        <Skeleton variant="rounded" width={28} height={28} sx={{ borderRadius: '8px' }} />
        <Grid sx={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0 }}>
          <Skeleton variant="rounded" width={28} height={28} sx={{ borderRadius: '6px' }} />
          <Skeleton variant="text" width={index % 2 ? '48%' : '62%'} height={28} />
        </Grid>
        <Skeleton variant="rounded" width={38} height={38} sx={{ borderRadius: '8px' }} />
      </Grid>
    ))}
  </>
);

const FileCabinetPickerRow: FC<PickerRowProps> = ({
  item,
  projectId,
  level,
  attachedIds,
  expandedFolderIds,
  selectedFiles,
  sortAscending,
  onToggleFile,
  onToggleFolder,
}) => {
  const isFolder = item.type === FileCabinetTypeEnum.Folder;
  const isExpanded = isFolder && expandedFolderIds.has(item.id);
  const isAttached = attachedIds.has(item.id);
  const isSelected = !!selectedFiles[item.id] || isAttached;
  const isKnownEmptyFolder = isFolder && hasKnownEmptyFolder(item);
  const Icon = getRowIcon(item.type, item.mimeType);

  const { data: folderData, isLoading: isFolderLoading } = useGetFileCabinetFolderItemsQuery(
    { folderId: item.id, projectId },
    {
      skip: !isExpanded || !projectId || isKnownEmptyFolder,
      refetchOnMountOrArgChange: true,
    }
  );

  const childItems = useMemo(
    () => sortItems(folderData?.items || [], sortAscending),
    [folderData?.items, sortAscending]
  );

  const handleRowClick = () => {
    if (isFolder) {
      onToggleFolder(item.id);
      return;
    }

    if (!isAttached) {
      onToggleFile(item);
    }
  };

  return (
    <>
      <Grid
        sx={{
          display: 'grid',
          gridTemplateColumns: '42px minmax(0, 1fr) 44px',
          alignItems: 'center',
          padding: '14px 16px',
          paddingLeft: `${16 + level * 28}px`,
          borderBottom: '1px solid #EEF1F6',
          cursor: isAttached ? 'default' : 'pointer',
          minHeight: '64px',
        }}
        onClick={handleRowClick}
      >
        <SelectionBox
          selected={isFolder ? isExpanded : isSelected}
          disabled={!isFolder && isAttached}
        />

        <Grid sx={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0 }}>
          <Icon />
          <Typography
            sx={{
              fontSize: '16px',
              color: '#222631',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {item.name}
          </Typography>
        </Grid>

        {isFolder ? (
          <IconButton
            onClick={(event) => {
              event.stopPropagation();
              onToggleFolder(item.id);
            }}
            sx={{
              width: '38px',
              height: '38px',
              border: `1.5px solid ${isExpanded ? '#FF3B30' : '#536DFE'}`,
              borderRadius: '8px',
              color: isExpanded ? '#FF3B30' : '#536DFE',
              fontSize: '24px',
            }}
          >
            {isExpanded ? '-' : '+'}
          </IconButton>
        ) : (
          <IconButton
            disabled={isAttached}
            onClick={(event) => {
              event.stopPropagation();
              onToggleFile(item);
            }}
            sx={{
              width: '38px',
              height: '38px',
              border: `1.5px solid ${isSelected ? '#FF3B30' : '#536DFE'}`,
              borderRadius: '8px',
              color: isSelected ? '#FF3B30' : '#536DFE',
              fontSize: '24px',
              '&.Mui-disabled': {
                borderColor: '#D9DEEA',
                color: '#8C8F9D',
              },
            }}
          >
            {isSelected ? '-' : '+'}
          </IconButton>
        )}
      </Grid>

      {isExpanded && (
        <>
          {isFolderLoading ? (
            <LoadingRows count={2} level={level + 1} />
          ) : childItems.length ? (
            childItems.map((childItem) => (
              <FileCabinetPickerRow
                key={childItem.id}
                item={childItem}
                projectId={projectId}
                level={level + 1}
                attachedIds={attachedIds}
                expandedFolderIds={expandedFolderIds}
                selectedFiles={selectedFiles}
                sortAscending={sortAscending}
                onToggleFile={onToggleFile}
                onToggleFolder={onToggleFolder}
              />
            ))
          ) : (
            <Grid
              sx={{
                height: '56px',
                paddingLeft: `${44 + level * 28}px`,
                borderBottom: '1px solid #EEF1F6',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Typography sx={{ color: '#8C8F9D', fontSize: '14px' }}>No files found</Typography>
            </Grid>
          )}
        </>
      )}
    </>
  );
};

const FileCabinetAttachmentPicker: FC<Props> = ({
  open,
  projectId,
  attachedFileCabinetIds,
  onClose,
  onAddFiles,
}) => {
  const [searchValue, setSearchValue] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<Record<number, IFolder>>({});
  const [expandedFolderIds, setExpandedFolderIds] = useState<Set<number>>(new Set());
  const [isAdding, setIsAdding] = useState(false);
  const [sortAscending, setSortAscending] = useState(true);
  const [error, setError] = useState('');

  const { data: rootData, isLoading: isRootLoading } = useGetProjectFileCabinetByIdQuery(
    { projectId },
    {
      skip: !open || !projectId,
      refetchOnMountOrArgChange: true,
    }
  );

  const attachedIds = useMemo(() => new Set(attachedFileCabinetIds), [attachedFileCabinetIds]);
  const filteredItems = useMemo(
    () => sortAndFilterItems(rootData?.items || [], searchValue, sortAscending),
    [rootData?.items, searchValue, sortAscending]
  );

  const selectedCount = Object.keys(selectedFiles).length;

  const resetAndClose = () => {
    setSearchValue('');
    setSelectedFiles({});
    setExpandedFolderIds(new Set());
    setSortAscending(true);
    setError('');
    onClose();
  };

  const toggleFolder = (folderId: number) => {
    const wasExpanded = expandedFolderIds.has(folderId);

    setExpandedFolderIds((prev) => {
      const next = new Set(prev);
      if (next.has(folderId)) {
        next.delete(folderId);
      } else {
        next.add(folderId);
      }
      return next;
    });

    if (wasExpanded) {
      setSelectedFiles((selected) => {
        const nextSelected = { ...selected };
        Object.values(nextSelected).forEach((file) => {
          if (isInsideFolder(file, folderId)) {
            delete nextSelected[file.id];
          }
        });
        return nextSelected;
      });
    }
  };

  const toggleFile = (file: IFolder) => {
    if (attachedIds.has(file.id)) return;

    setSelectedFiles((prev) => {
      const next = { ...prev };
      if (next[file.id]) {
        delete next[file.id];
      } else {
        next[file.id] = file;
      }
      return next;
    });
  };

  const handleAddFiles = async () => {
    const files = Object.values(selectedFiles);
    if (!files.length) return;

    setIsAdding(true);
    setError('');

    try {
      const resolved = await resolveFileCabinetIds(
        projectId,
        files.map((file) => file.id)
      );
      const resolvedById = new Map(resolved.items.map((item) => [item.id, item]));
      const mediaItems = files
        .map((file) => {
          const resolvedFile = resolvedById.get(file.id);
          const url = resolvedFile?.url || file.url || file.path;
          if (!url) return null;
          return toMediaItem(file, url, resolvedFile?.mimeType);
        })
        .filter((item): item is IMediaItem => !!item);

      if (!mediaItems.length) {
        setError('Unable to resolve selected files.');
        return;
      }

      onAddFiles(mediaItems);
      resetAndClose();
    } catch {
      setError('Unable to add selected files. Please try again.');
    } finally {
      setIsAdding(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={resetAndClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          maxWidth: '980px',
        },
      }}
    >
      <Grid sx={{ padding: '32px 44px 24px' }}>
        <Grid sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography sx={{ fontSize: '30px', fontWeight: 400, color: '#222631' }}>
            Select a File
          </Typography>
          <IconButton onClick={resetAndClose} size="small">
            <CloseIcon />
          </IconButton>
        </Grid>
      </Grid>

      <Grid sx={{ borderTop: '1px solid #E9ECF4', padding: '24px 44px 32px' }}>
        <Grid sx={{ display: 'flex', gap: '18px', marginBottom: '22px' }}>
          <TextField
            value={searchValue}
            onChange={(event) => setSearchValue(event.target.value)}
            placeholder="Search"
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                height: '46px',
                borderRadius: '10px',
                color: '#222631',
              },
            }}
          />
          <Button
            variant="outlined"
            startIcon={<SortIcon />}
            onClick={() => setSortAscending((prev) => !prev)}
            sx={{
              minWidth: '140px',
              height: '46px',
              borderColor: '#E2E6F0',
              borderRadius: '10px',
              color: '#222631',
              textTransform: 'none',
              fontSize: '17px',
            }}
          >
            Sorting
          </Button>
        </Grid>

        <Grid
          sx={{
            border: '1px solid #E9ECF4',
            borderRadius: '14px',
            overflow: 'hidden',
          }}
        >
          <Grid sx={{ padding: '18px 16px', borderBottom: '1px solid #E9ECF4' }}>
            <Typography sx={{ fontSize: '17px', fontWeight: 600, color: '#222631' }}>
              Item
            </Typography>
          </Grid>

          <Grid sx={{ maxHeight: '340px', overflowY: 'auto' }}>
            {isRootLoading ? (
              <LoadingRows count={5} />
            ) : filteredItems.length ? (
              filteredItems.map((item) => (
                <FileCabinetPickerRow
                  key={item.id}
                  item={item}
                  projectId={projectId}
                  level={0}
                  attachedIds={attachedIds}
                  expandedFolderIds={expandedFolderIds}
                  selectedFiles={selectedFiles}
                  sortAscending={sortAscending}
                  onToggleFile={toggleFile}
                  onToggleFolder={toggleFolder}
                />
              ))
            ) : (
              <Grid
                sx={{
                  height: '220px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography sx={{ color: '#8C8F9D' }}>No files found</Typography>
              </Grid>
            )}
          </Grid>
        </Grid>

        {error && (
          <Typography sx={{ color: '#FF3B30', marginTop: '16px', fontSize: '14px' }}>
            {error}
          </Typography>
        )}

        <Grid
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginTop: '24px',
          }}
        >
          <Typography sx={{ fontSize: '17px', fontWeight: 600 }}>
            {selectedCount} {selectedCount === 1 ? 'item' : 'items'} selected
          </Typography>
          <Grid sx={{ display: 'flex', gap: '14px' }}>
            <Button
              variant="outlined"
              onClick={resetAndClose}
              sx={{
                height: '52px',
                minWidth: '104px',
                borderRadius: '10px',
                textTransform: 'none',
                fontSize: '17px',
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              disabled={!selectedCount || isAdding}
              onClick={handleAddFiles}
              sx={{
                height: '52px',
                minWidth: '138px',
                borderRadius: '10px',
                textTransform: 'none',
                fontSize: '17px',
                backgroundColor: '#536DFE',
              }}
            >
              {isAdding ? 'Adding...' : 'Add Files'}
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </Dialog>
  );
};

export default FileCabinetAttachmentPicker;
